// src/features/apps/useBuildReadiness.ts

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { BUILD_READINESS_QUERY_KEYS, BUILD_READINESS_STALE_MS } from '@/lib/constants/buildReadiness';
import { AppFactoryApi } from './AppFactoryApi';

/**
 * The factory's build admission as a read, for one platform and the release the
 * Build button would build (build readiness, 3.34.5; appfactory ADR-0040).
 * Asked only once a release is known. Like every factory read it degrades
 * quietly — no retry, and an error (an older api or daemon answers 404) means
 * "unknown": the Status tab then keeps today's behaviour. Short-lived and
 * re-read on window focus, because a prerequisite moves on another sub-tab or
 * page (the content tree most of all); the mutations that change one on this
 * page invalidate it directly (useAppFactory / useAppMutations).
 */
export function useBuildReadiness(appCfgId: number, platform: string, shellReleaseId: string | null) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: BUILD_READINESS_QUERY_KEYS.read(selectedCId ?? 0, appCfgId, platform, shellReleaseId ?? ''),
    queryFn: () => AppFactoryApi.getBuildReadiness(selectedCId!, appCfgId, platform, shellReleaseId!),
    enabled: selectedCId != null && appCfgId > 0 && shellReleaseId != null,
    retry: false,
    staleTime: BUILD_READINESS_STALE_MS,
    refetchOnWindowFocus: true,
  });
}
